import type { CrudAdapter } from '@botto/core';
import { useCrudDataSource } from '@botto/core';
import { smsClient } from '../client';
import type { GroupDomain, GroupDomainCreate, GroupDomainUpdate } from '../types';

export function createGroupDomainsAdapter(
  routingChoiceId: number,
): CrudAdapter<GroupDomain, GroupDomainCreate, GroupDomainUpdate> {
  return {
    async list({ offset, limit, sortName, sortDirection }) {
      const { data, error } = await smsClient.GET(
        '/routing_choices/{routing_choice_id}/group_domains/',
        {
          params: {
            path: { routing_choice_id: routingChoiceId },
            query: {
              offset,
              limit,
              'sort.name': sortName ?? 'id',
              'sort.direction': (sortDirection ?? 'asc') as 'asc' | 'desc',
            },
          },
        },
      );
      if (error) throw error;
      return { items: data?.items ?? [], total: data?.total ?? 0 };
    },
    async create(payload) {
      const body: GroupDomainCreate = {
        ...payload,
        routing_choice_id: routingChoiceId,
      };
      const { data, error } = await smsClient.POST(
        '/routing_choices/{routing_choice_id}/group_domains/',
        {
          params: { path: { routing_choice_id: routingChoiceId } },
          body,
        },
      );
      if (error) throw error;
      return { id: data?.id ?? 0 };
    },
    async update(payload) {
      const { data, error } = await smsClient.PUT(
        '/routing_choices/{routing_choice_id}/group_domains/',
        {
          params: { path: { routing_choice_id: routingChoiceId } },
          body: { ...payload, routing_choice_id: routingChoiceId },
        },
      );
      if (error) throw error;
      return (data ?? payload) as GroupDomain;
    },
    async byKey(id) {
      const { data, error } = await smsClient.GET(
        '/routing_choices/{routing_choice_id}/group_domains/{item_id}',
        {
          params: {
            path: { routing_choice_id: routingChoiceId, item_id: Number(id) },
          },
        },
      );
      if (error) throw error;
      return data as GroupDomain;
    },
    async remove(id) {
      const { error } = await smsClient.DELETE(
        '/routing_choices/{routing_choice_id}/group_domains/{item_id}',
        {
          params: {
            path: { routing_choice_id: routingChoiceId, item_id: Number(id) },
          },
        },
      );
      if (error) throw error;
    },
  };
}

export function createGroupDomainsStore(routingChoiceId: number) {
  return useCrudDataSource(createGroupDomainsAdapter(routingChoiceId));
}
